// =============================================================================
// ComplianceRow — sekcja „Zgodnosc": 6 chipow G1-G6 w jednej linii, liczby,
// przycisk „Analizuj"; rozwiniete = ParityReportTable (paginacja, filtr uwag).
// =============================================================================

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Loader2, RefreshCw, CheckCircle2, XCircle, AlertTriangle, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { ParityReport, RenderedFile, SiteModel, StaticRedirectRow, StaticSiteRow, MigrationProgressEvent } from "@/services/staticSite/types";
import { runAnalysis } from "@/services/staticSite/migrationRunner";
import { ParityReportTable } from "@/components/thematic-maps/migration/ParityReportTable";
import { StudioSection } from "./StudioSection";

interface Props {
  site: StaticSiteRow;
  redirects: StaticRedirectRow[];
  onAddRedirect: (row: Omit<StaticRedirectRow, "id" | "site_id">) => Promise<void>;
  onRemoveRedirect: (id: string) => Promise<void>;
  onAnalyzed?: () => void;
}

export function ComplianceRow({ site, redirects, onAddRedirect, onRemoveRedirect, onAnalyzed }: Props) {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [report, setReport] = useState<ParityReport | null>(site.last_parity || null);
  const [model, setModel] = useState<SiteModel | null>(null);
  const [files, setFiles] = useState<RenderedFile[]>([]);

  const run = async () => {
    setBusy(true);
    setStatus("Start…");
    try {
      const res = await runAnalysis(site, (ev: MigrationProgressEvent) => {
        if (ev.type === "phase") setStatus(ev.message);
        else if (ev.type === "progress") setStatus(`${ev.message || ""} ${ev.current}/${ev.total}`);
        else if (ev.type === "done") setStatus("");
        else if (ev.type === "error") setStatus(`Błąd: ${ev.message}`);
      });
      setReport(res.report);
      setModel(res.site);
      setFiles(res.files);
      onAnalyzed?.();
      toast({
        title: res.report.blocking ? "Analiza: są blokady" : "Analiza: bez blokad",
        description: `${res.report.summary.pages} stron, ${res.report.summary.moneyLinksTotal} money linków, ${res.report.summary.missingUrls} brakujących adresów`,
        variant: res.report.blocking ? "destructive" : undefined,
      });
    } catch (e) {
      toast({ title: "Analiza nieudana", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const gates = report?.gates || [];
  const withIssues = report ? report.pages.filter((p) => p.issues.length).length : 0;

  const summary = report ? (
    <>
      <span className="flex items-center gap-1">
        {gates.map((g) => (
          <Badge
            key={g.id}
            variant="outline"
            title={`${g.label}${g.issues.length ? `: ${g.issues.slice(0, 2).join(" | ")}` : ""}`}
            className={`gap-1 px-1.5 text-[10px] ${g.passed ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400" : g.level === "hard" ? "border-red-500/50 text-red-600 dark:text-red-400" : "border-amber-500/50 text-amber-600 dark:text-amber-400"}`}
          >
            {g.passed ? <CheckCircle2 className="h-3 w-3" /> : g.level === "hard" ? <XCircle className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
            {g.id}
          </Badge>
        ))}
      </span>
      <span className="text-xs text-muted-foreground">{report.summary.pages} stron · {withIssues} z uwagami · {report.summary.missingUrls} brakujących adresów</span>
      {site.analyzed_at && <span className="text-xs text-muted-foreground">analiza {new Date(site.analyzed_at).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" })}</span>}
      {status && <span className="text-xs text-muted-foreground">{status}</span>}
    </>
  ) : (
    <span className="text-xs text-muted-foreground">{status || "Brak analizy — uruchom, żeby sprawdzić linki sprzedażowe, adresy i meta."}</span>
  );

  return (
    <StudioSection
      icon={ShieldCheck}
      title="Zgodność"
      collapsible
      help="G1: każdy link sprzedażowy bajt w bajt. G2: liczba linków wychodzących. G3: każdy adres z WordPressa ma plik albo 301. G5: title, description, canonical. G4/G6 tylko ostrzegają. Blokada = brak deployu."
      summary={summary}
      actions={
        <Button size="sm" variant="outline" onClick={() => void run()} disabled={busy}>
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : report ? <RefreshCw className="mr-2 h-4 w-4" /> : <Search className="mr-2 h-4 w-4" />}
          {report ? "Analizuj ponownie" : "Uruchom analizę"}
        </Button>
      }
      defaultOpen={Boolean(report?.blocking)}
    >
      {report ? (
        <>
          <ParityReportTable report={report} site={model} files={files} redirects={redirects} onAddRedirect={onAddRedirect} onRemoveRedirect={onRemoveRedirect} />
          {!model && <p className="mt-2 text-xs text-muted-foreground">Raport z poprzedniej analizy. Uruchom ponownie, żeby zobaczyć podgląd renderu per strona.</p>}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Uruchom analizę, żeby zobaczyć raport.</p>
      )}
    </StudioSection>
  );
}
